<template>
  <div class="billing-list">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2>{{ $t("billing.title") }}</h2>
      <div class="d-flex gap-2">
        <b-button variant="success" @click="toggleAddForm">
          <i class="fas" :class="showAddForm ? 'fa-minus' : 'fa-plus'"></i>
          {{ showAddForm ? $t("billing.cancel_add") : $t("billing.add_charge") }}
        </b-button>
        <b-button variant="primary" @click="showCheckoutBillModal">
          <i class="fas fa-file-invoice"></i> {{ $t("billing.generate_checkout_bill") }}
        </b-button>
      </div>
    </div>

    <!-- Filters -->
    <b-card class="mb-4">
      <div class="row">
        <div class="col-md-3">
          <b-form-group :label="$t('billing.search')">
            <b-form-input
              v-model="filters.search"
              :placeholder="$t('billing.search_placeholder')"
              @input="debounceSearch"
            ></b-form-input>
          </b-form-group>
        </div>
        <div class="col-md-3">
          <b-form-group :label="$t('billing.category')">
            <b-form-select
              v-model="filters.category"
              :options="categoryOptions"
              @change="fetchBillings"
            ></b-form-select>
          </b-form-group>
        </div>
        <div class="col-md-3">
          <b-form-group :label="$t('billing.date_from')">
            <b-form-datepicker
              v-model="filters.date_from"
              @input="fetchBillings"
            ></b-form-datepicker>
          </b-form-group>
        </div>
        <div class="col-md-3">
          <b-form-group :label="$t('billing.date_to')">
            <b-form-datepicker
              v-model="filters.date_to"
              @input="fetchBillings"
            ></b-form-datepicker>
          </b-form-group>
        </div>
      </div>
    </b-card>

    <!-- Inline Add Charge Form -->
    <b-card v-if="showAddForm" class="mb-4 border-success">
      <template #header>
        <h5 class="mb-0">
          <i class="fas fa-plus text-success"></i>
          {{ $t("billing.add_new_charge") }}
        </h5>
      </template>

      <b-form @submit.prevent="saveNewCharge">
        <div class="row">
          <div class="col-md-6">
            <b-form-group :label="$t('billing.room')" label-for="room-select" required>
              <b-form-select
                id="room-select"
                v-model="newCharge.booking_room_type_room_id"
                :options="roomOptions"
                :disabled="loadingRooms"
                required
              >
                <template #first>
                  <b-form-select-option value="" disabled>
                    {{
                      loadingRooms
                        ? $t("billing.loading_rooms")
                        : $t("billing.select_room_required")
                    }}
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </div>

          <div class="col-md-6">
            <b-form-group
              :label="$t('billing.category')"
              label-for="category-select"
              required
            >
              <b-form-select
                id="category-select"
                v-model="newCharge.category"
                :options="categoryOptions"
                required
              >
                <template #first>
                  <b-form-select-option value="" disabled>
                    {{ $t("billing.select_category") }}
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <b-form-group :label="$t('billing.item_name')" label-for="item-name" required>
              <b-form-input
                id="item-name"
                v-model="newCharge.item_name"
                type="text"
                :placeholder="$t('billing.enter_item_name')"
                required
                :state="newCharge.item_name ? true : null"
              ></b-form-input>
            </b-form-group>
          </div>

          <div class="col-md-6">
            <b-form-group :label="$t('billing.description')" label-for="description">
              <b-form-textarea
                id="description"
                v-model="newCharge.description"
                rows="2"
                :placeholder="$t('billing.enter_description_optional')"
              ></b-form-textarea>
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <b-form-group :label="$t('billing.issued_by')" label-for="issued-by">
              <b-form-input
                id="issued-by"
                v-model="newCharge.issued_by"
                type="text"
                :placeholder="$t('billing.enter_issuer_name')"
                :state="newCharge.issued_by ? true : null"
              ></b-form-input>
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4">
            <b-form-group :label="$t('billing.quantity')" label-for="quantity" required>
              <b-form-input
                id="quantity"
                v-model.number="newCharge.quantity"
                type="number"
                min="1"
                step="1"
                :placeholder="$t('billing.enter_quantity')"
                required
                :state="newCharge.quantity > 0 ? true : null"
              ></b-form-input>
            </b-form-group>
          </div>

          <div class="col-md-4">
            <b-form-group :label="$t('billing.unit_price')" label-for="amount" required>
              <b-input-group :prepend="$t('billing.currency_symbol')">
                <b-form-input
                  id="amount"
                  v-model.number="newCharge.amount"
                  type="number"
                  min="0"
                  step="0.01"
                  :placeholder="$t('billing.enter_price')"
                  required
                  :state="newCharge.amount > 0 ? true : null"
                ></b-form-input>
              </b-input-group>
            </b-form-group>
          </div>

          <div class="col-md-4">
            <b-form-group :label="$t('billing.paid_amount')" label-for="paid-amount">
              <b-input-group :prepend="$t('billing.currency_symbol')">
                <b-form-input
                  id="paid-amount"
                  v-model.number="newCharge.paid_amount"
                  type="number"
                  min="0"
                  step="0.01"
                  :placeholder="$t('billing.enter_paid_amount')"
                  :state="newCharge.paid_amount >= 0 ? true : null"
                ></b-form-input>
              </b-input-group>
            </b-form-group>
          </div>

          <div class="col-md-4">
            <b-form-group :label="$t('billing.total_amount')">
              <b-form-input
                :value="formatCurrency(calculateTotal())"
                readonly
                variant="outline-secondary"
              ></b-form-input>
            </b-form-group>
          </div>
        </div>

        <div class="d-flex justify-content-end gap-2">
          <b-button variant="secondary" @click="cancelAddForm" :disabled="saving">
            <i class="fas fa-times"></i> {{ $t("billing.cancel") }}
          </b-button>
          <b-button variant="success" type="submit" :disabled="!canSave || saving">
            <b-spinner v-if="saving" small class="me-1"></b-spinner>
            <i v-else class="fas fa-save"></i>
            {{ saving ? $t("billing.saving") : $t("billing.save_charge") }}
          </b-button>
        </div>
      </b-form>
    </b-card>

    <!-- Billing Table -->
    <b-table :items="billings" :fields="fields" :busy="loading" striped hover responsive>
      <template #table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong>{{ $t("billing.loading") }}</strong>
        </div>
      </template>

      <template #cell(booking)="data">
        <div>
          <strong>{{ $t("billing.booking_id") }}:</strong> #{{ data.item.booking.id
          }}<br />
          <strong>{{ $t("billing.hotel") }}:</strong>
          {{ data.item.booking.hotel?.name }}<br />
          <strong>{{ $t("billing.guest") }}:</strong>
          {{ data.item.booking.user?.full_name }}
        </div>
      </template>

      <template #cell(room)="data">
        <div>
          <strong>{{ $t("billing.room") }}:</strong> #{{ data.item.room?.number }}<br />
          <strong>{{ $t("billing.type") }}:</strong> {{ data.item.room?.room_type?.name }}
        </div>
      </template>

      <template #cell(amount)="data">
        {{ $t("billing.currency_symbol") }}{{ parseFloat(data.item.amount).toFixed(2) }}
      </template>

      <template #cell(total_amount)="data">
        <strong
          >{{ $t("billing.currency_symbol")
          }}{{ parseFloat(data.item.total_amount).toFixed(2) }}</strong
        >
      </template>

      <template #cell(category)="data">
        {{ data.item.category_name }}
      </template>

      <template #cell(actions)="data">
        <div class="d-flex gap-1">
          <b-button
            size="sm"
            variant="danger"
            @click="deleteBilling(data.item)"
            :title="$t('billing.delete')"
          >
            <i class="fas fa-trash"></i>
          </b-button>
        </div>
      </template>
    </b-table>

    <!-- Pagination -->
    <b-pagination
      v-if="pagination.total > pagination.per_page"
      v-model="pagination.current_page"
      :total-rows="pagination.total"
      :per-page="pagination.per_page"
      @change="onPageChange"
      align="center"
    ></b-pagination>

    <!-- Add/Edit Modal -->
    <BillingModal
      v-if="showModal"
      :show.sync="showModal"
      :billing="selectedBilling"
      @saved="onBillingSaved"
    />

    <!-- Booking Charges Modal -->
    <BookingChargesModal v-if="showBookingModal" :show.sync="showBookingModal" />

    <!-- Checkout Bill Modal -->
    <b-modal
      id="checkout-bill-modal"
      :title="$t('billing.select_booking')"
      @ok="generateCheckoutBill(selectedBookingId)"
      :ok-title="$t('billing.generate')"
      :cancel-title="$t('billing.cancel')"
      :ok-disabled="!selectedBookingId"
    >
      <b-form-group :label="$t('billing.booking')">
        <b-form-select v-model="selectedBookingId" :options="bookingOptions" required>
          <template #first>
            <b-form-select-option value="" disabled>
              {{ $t("billing.select_booking") }}
            </b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import BillingModal from "./BillingModal.vue";
import BookingChargesModal from "./BookingChargesModal.vue";

export default {
  name: "BillingList",
  components: {
    BillingModal,
    BookingChargesModal,
  },
  data() {
    return {
      billings: [],
      loading: false,
      showModal: false,
      showBookingModal: false,
      showAddForm: false,
      saving: false,
      newCharge: {
        booking_id: null,
        booking_room_type_room_id: null,
        room_name: "",
        item_name: "",
        amount: 0,
        quantity: 1,
        description: "",
        category: "",
        paid_amount: 0,
        issued_by: "",
      },
      bookingOptions: [],
      roomOptions: [],
      loadingBookings: false,
      loadingRooms: false,
      selectedBilling: null,
      categories: {},
      filters: {
        search: "",
        category: "",
        date_from: null,
        date_to: null,
      },
      pagination: {
        current_page: 1,
        per_page: 15,
        total: 0,
      },
      searchTimeout: null,
      fields: [
        { key: "id", label: this.$t("billing.field.id"), sortable: true },
        { key: "booking", label: this.$t("billing.field.booking"), sortable: false },
        { key: "room", label: this.$t("billing.field.room"), sortable: false },
        { key: "item_name", label: this.$t("billing.field.item"), sortable: true },
        { key: "category", label: this.$t("billing.field.category"), sortable: true },
        { key: "quantity", label: this.$t("billing.field.qty"), sortable: true },
        { key: "amount", label: this.$t("billing.field.unit_price"), sortable: true },
        { key: "total_amount", label: this.$t("billing.field.total"), sortable: true },
        { key: "charged_at", label: this.$t("billing.field.date"), sortable: true },
        { key: "actions", label: this.$t("billing.field.actions"), sortable: false },
      ],
      selectedBookingId: null,
    };
  },
  computed: {
    categoryOptions() {
      const options = [{ value: "", text: "All Categories" }];
      Object.keys(this.categories).forEach((key) => {
        options.push({ value: key, text: this.categories[key] });
      });
      return options;
    },
    canSave() {
      return (
        this.newCharge.item_name.trim() &&
        this.newCharge.category &&
        this.newCharge.quantity > 0 &&
        this.newCharge.amount > 0
      );
    },
  },
  methods: {
    toggleAddForm() {
      this.showAddForm = !this.showAddForm;
      this.resetNewCharge();
    },

    calculateTotal() {
      return this.newCharge.quantity * this.newCharge.amount;
    },

    formatCurrency(value) {
      return Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
        value
      );
    },

    resetNewCharge() {
      this.newCharge = {
        booking_id: null,
        booking_room_type_room_id: null,
        room_name: "",
        item_name: "",
        amount: 0,
        quantity: 1,
        description: "",
        category: "",
        paid_amount: 0,
        issued_by: "",
      };
    },

    cancelAddForm() {
      this.toggleAddForm();
    },

    async fetchBookings() {
      this.loadingBookings = true;
      try {
        const response = await request({
          method: "get",
          url: "/bookings/active",
        });
        this.bookingOptions = response.data.map((booking) => ({
          value: booking.id,
          text: `Booking #${booking.id}: ${booking.user.full_name}`,
        }));
      } catch (error) {
        console.error("Error fetching bookings:", error);
      } finally {
        this.loadingBookings = false;
      }
    },

    async fetchBookedRooms() {
      this.loadingRooms = true;
      try {
        const response = await request({
          method: "get",
          url: "/booked_rooms/active",
        });
        this.roomOptions = response.data.map((bookingRoomTypeRoom) => ({
          value: bookingRoomTypeRoom.id,
          text: `Room #${bookingRoomTypeRoom.room.room_number}: ${bookingRoomTypeRoom.booking_room_type.room_type.name}`,
        }));
      } catch (error) {
        console.error("Error fetching rooms:", error);
      } finally {
        this.loadingRooms = false;
      }
    },

    async saveNewCharge() {
      if (!this.canSave) return;
      this.saving = true;
      try {
        await request({
          method: "post",
          url: "/billings",
          data: this.newCharge,
        });
        this.$bvToast.toast(this.$t("billing.charge_added_successfully"), {
          title: this.$t("common.success"),
          variant: "success",
          solid: true,
        });
        this.toggleAddForm();
        this.fetchBillings();
      } catch (error) {
        console.error("Error saving new charge:", error);
        this.$bvToast.toast(this.$t("billing.failed_to_add_charge"), {
          title: this.$t("common.error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.saving = false;
      }
    },

    async fetchBillings() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
          ...this.filters,
        };

        const response = await request({
          method: "get",
          url: "/billings",
          params,
        });

        this.billings = response.data.data || response.data;
        
        if (response.data.meta) {
          this.pagination = {
            current_page: response.data.meta.current_page,
            per_page: response.data.meta.per_page,
            total: response.data.meta.total,
          };
        }
      } catch (error) {
        console.error("Error fetching billings:", error);
        this.$bvToast.toast(this.$t("billing.failed_to_fetch"), {
          title: this.$t("common.error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },

    async fetchCategories() {
      try {
        const response = await request({
          method: "get",
          url: "/billing/categories",
        });
        this.categories = response.data;
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    },

    showAddModal() {
      this.selectedBilling = null;
      this.showModal = true;
    },

    editBilling(billing) {
      this.selectedBilling = billing;
      this.showModal = true;
    },

    async deleteBilling(billing) {
      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          this.$t("billing.confirm_delete"),
          {
            title: this.$t("common.confirm_delete"),
            size: "sm",
            buttonSize: "sm",
            okVariant: "danger",
            okTitle: this.$t("common.delete"),
            cancelTitle: this.$t("common.cancel"),
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true,
          }
        );

        if (confirmed) {
          await request({
            method: "delete",
            url: `/billings/${billing.id}`,
          });
          this.$bvToast.toast(this.$t("billing.deleted_successfully"), {
            title: this.$t("common.success"),
            variant: "success",
            solid: true,
          });
          this.fetchBillings();
        }
      } catch (error) {
        console.error("Error deleting billing:", error);
        this.$bvToast.toast(this.$t("billing.failed_to_delete"), {
          title: this.$t("common.error"),
          variant: "danger",
          solid: true,
        });
      }
    },

    showCheckoutBillModal() {
      this.selectedBookingId = null;
      this.$bvModal.show("checkout-bill-modal");
    },

    async generateCheckoutBill(bookingId) {
      try {
        const response = await request({
          method: "get",
          url: `/bookings/${bookingId}/checkout-bill`,
        });

        this.$bvToast.toast(this.$t("billing.checkout_generated_successfully"), {
          title: this.$t("common.success"),
          variant: "success",
          solid: true,
        });
        if (response && response.pdf_url) {
          window.open(response.pdf_url, "_blank");
        }
      } catch (error) {
        console.error("Error generating checkout bill:", error);
        this.$bvToast.toast(this.$t("billing.failed_to_generate_checkout"), {
          title: this.$t("common.error"),
          variant: "danger",
          solid: true,
        });
      }
    },

    closeModal() {
      this.showModal = false;
      this.selectedBilling = null;
    },

    onBillingSaved() {
      this.closeModal();
      this.fetchBillings();
    },

    onPageChange(page) {
      this.pagination.current_page = page;
      this.fetchBillings();
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchBillings();
      }, 500);
    },

    getCategoryVariant() {
      const variants = {
        default: "dark",
      };
      return variants.default;
    },
  },

  async created() {
    await this.fetchCategories();
    await this.fetchBillings();
    await this.fetchBookings();
    await this.fetchBookedRooms();
  },
};
</script>

<style scoped>
.billing-list {
  padding: 20px;
}
</style>
